<template>
    <div class="fb-panel">
        <div class="fb-panel-head">
            <FileBrowserPath :breadcrumbItems="$store.state.breadcrumbs" />
        </div>
        <div v-if="error || truncated" class="fb-panel-body">
            <div v-if="error" class="fb-notice fb-notice-danger">
                <i class="fa-solid fa-circle-exclamation"></i>
                <span>{{ error }}</span>
            </div>
            <div v-else class="fb-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span>Showing the first {{ maxResults.toLocaleString() }} objects. Open a folder to narrow the
                    listing.</span>
            </div>
        </div>
        <!-- A folder's shape at a glance, so the listing does not have to be counted -->
        <div v-if="!loading && !error && filesList.length" class="fb-summary">
            <span class="fb-summary-item">
                <i class="fa-solid fa-folder fb-glyph-folder"></i>{{ folderCount }} {{ folderCount === 1 ? 'folder' :
                    'folders' }}
            </span>
            <span class="fb-summary-item">
                <i class="fa-solid fa-file fb-glyph-muted"></i>{{ fileCount }} {{ fileCount === 1 ? 'file' : 'files' }}
            </span>
            <span v-if="totalSize" class="fb-summary-item">
                <i class="fa-solid fa-database fb-glyph-muted"></i>{{ totalSize }}
            </span>
        </div>
        <FileBrowserTable :tableId="tableId" :data="filesList" :loading="loading" @open="open" />
    </div>
</template>
<script>
/*
 * Objects for one folder, listed through pail. The credential and bucket are path
 * segments on the request and the folder key travels as `prefix`, so the display URI
 * held in `folderPath` is split apart at the call site.
 */
import FileBrowserPath from "../../components/filebrowser/FileBrowserPath.vue";
import FileBrowserTable from "../../components/filebrowser/FileBrowserTable.vue";
import { mapActions } from "vuex";
import { formatBytes } from "../../utils/commonFunction";
import {
    MAX_LIST_RESULTS,
    buildStoragePath,
    normalizeObjects,
    parseStoragePath,
    schemeForCredentialType,
} from "../../utils/fileBrowser";

export default {
    components: { FileBrowserPath, FileBrowserTable },
    name: "FolderView",
    data() {
        return {
            filesList: [],
            loading: false,
            error: null,
            truncated: false,
            maxResults: MAX_LIST_RESULTS,
            // guards against the route watchers listing the same folder twice
            lastFetchKey: null,
        };
    },
    computed: {
        folderPath() {
            return this.$store.state.amdp.folderPath;
        },
        credential() {
            return this.$store.state.amdp.selectedCredential;
        },
        scheme() {
            return schemeForCredentialType(this.$store.state.amdp.selectedCredentialType);
        },
        tableId() {
            return this.$route.params.id || this.$route.params.bucket || "fileBrowser";
        },
        folderCount() {
            return this.filesList.filter((item) => item.is_dir).length;
        },
        fileCount() {
            return this.filesList.length - this.folderCount;
        },
        totalSize() {
            const bytes = this.filesList.reduce((sum, item) => sum + (item.is_dir ? 0 : item.size), 0);
            return bytes ? formatBytes(bytes) : "";
        },
    },
    watch: {
        '$route.params.id': {
            handler: function (to, from) {
                if ((!from && !to) || from !== to) this.resolveFolderPath();
            },
            immediate: true
        },
        '$route.query.refresh': function (to, from) {
            if (to && to !== from) this.fetchFileBrowserList(this.folderPath, true);
        },
        // A new bucket or credential re-roots the listing even when the folder id does
        // not change, so the accumulated path is discarded and rebuilt from the route.
        // These watch the route rather than the store so the bucket param is already the
        // new one by the time they fire.
        '$route.params.bucket': function (to, from) {
            if (to && to !== from) this.rebase();
        },
        '$route.query.credential': function (to, from) {
            if (to && to !== from) this.rebase();
        },
    },
    methods: {
        ...mapActions({
            getFileBrowserList: "amdp/getFileBrowserList",
        }),
        // "slash" stands in for an entry whose name is literally "/", which cannot be a
        // route param on its own - that segment contributes only the separator
        appendSegment(path, id) {
            return id === "slash" ? `${path}/` : `${path}${id}/`;
        },
        rebase() {
            this.$store.dispatch("amdp/setFolderPath", "");
            this.resolveFolderPath();
        },
        resolveFolderPath() {
            const id = this.$route.params.id;
            let path = this.folderPath;
            // no accumulated path (bucket root, reload, credential switch) - rebuild from
            // the route; otherwise the route id is the next folder down
            if (!path || !this.scheme || !path.startsWith(`${this.scheme}://`)) {
                path = buildStoragePath(this.scheme, this.$route.params.bucket);
                if (id) path = this.appendSegment(path, id);
            } else if (id) {
                path = this.appendSegment(path, id);
            } else {
                return;
            }
            this.$store.dispatch("amdp/setFolderPath", path);
            this.fetchFileBrowserList(path);
        },
        // A folder descends into the route and extends the trail; a file has its own page.
        open(row) {
            const id = row.folderName !== "" ? row.folderName : "slash";
            const query = this.carriedQuery();
            // both routes declare :cid and :bucket, so a push by name has to carry them
            const params = { cid: this.$route.params.cid, bucket: this.$route.params.bucket, id };
            const name = row.is_dir ? "File Browser Folder" : "File Browser View";
            // Files join the trail too: without a crumb of their own the file page ends on
            // its parent folder, which renders as the current segment and leaves no way
            // back up. `isFile` keeps the path bar from giving it a folder's trailing slash.
            this.$store.dispatch("setBreadcrumbs", [...this.$store.state.breadcrumbs, {
                name: row.folderName !== "" ? row.folderName : "/",
                route: { name, params, query },
                isFile: !row.is_dir,
            }]);
            this.$router.push({ name, params: { ...params, data: JSON.stringify(row) }, query });
        },
        // the one-shot refresh token must not survive into the next route
        carriedQuery() {
            const { refresh, ...carried } = this.$route.query;
            return carried;
        },
        async fetchFileBrowserList(folderPath, force = false) {
            const parsed = parseStoragePath(folderPath);
            if (!parsed || !this.credential) return;

            const fetchKey = `${this.credential}|${folderPath}`;
            if (!force && fetchKey === this.lastFetchKey) return;
            this.lastFetchKey = fetchKey;

            this.error = null;
            this.truncated = false;
            this.filesList = [];
            this.loading = true;
            let response;
            try {
                response = await this.getFileBrowserList({
                    credential: this.credential,
                    bucket: parsed.bucket,
                    prefix: parsed.key,
                    maxResults: MAX_LIST_RESULTS,
                });
            } catch (error) {
                this.filesList = [];
                this.loading = false;
                this.lastFetchKey = null; // let a retry through
                this.error = error.response?.data?.error || error.message;
                return;
            }
            this.filesList = normalizeObjects(response.data?.items, parsed.key);
            this.truncated = !!response.data?.nextPageToken;
            this.loading = false;
        }
    }
};
</script>

<style scoped>
.fb-panel-body {
    padding: 1.25rem 1.25rem 0;
}

/* Counts sit between the path and the rows, reading as a caption for the listing */
.fb-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
    padding: 0.7rem 1.25rem;
    border-bottom: 1px solid var(--fb-border);
    font-size: 12px;
    color: var(--fb-text-muted);
}

.fb-summary-item {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
}
</style>
